import { Button, Text } from "@chakra-ui/react";
import { SSO_RETURN_PATH_KEY, useGetSsoAuthorizationUrlMutation } from "@tether-web-portal/api/sso/hooks";
import { SsoProviderType } from "@tether-web-portal/api/sso/types";

interface SsoLoginButtonProps {
  providerType: SsoProviderType;
  organisationId: string;
  onError: (error: Error) => void;
  onAttemptStart?: () => void;
}

interface ProviderBranding {
  label: string;
  icon: JSX.Element;
  bg: string;
  hoverBg: string;
  activeBg: string;
  color: string;
}

const MicrosoftLogo = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 21 21">
    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
  </svg>
);

const PROVIDER_BRANDING: Record<SsoProviderType, ProviderBranding> = {
  [SsoProviderType.AZURE_AD]: {
    label: "Sign in with Microsoft",
    icon: <MicrosoftLogo />,
    bg: "#2F2F2F",
    hoverBg: "#404040",
    activeBg: "#1a1a1a",
    color: "white",
  },
};

const FALLBACK_BRANDING: ProviderBranding = {
  label: "Sign in with SSO",
  icon: <></>,
  bg: "blue.500",
  hoverBg: "blue.600",
  activeBg: "blue.700",
  color: "white",
};

export default function SsoLoginButton({
  providerType,
  organisationId,
  onError,
  onAttemptStart,
}: SsoLoginButtonProps) {
  const authorizeMutation = useGetSsoAuthorizationUrlMutation();
  const isLoading = authorizeMutation.isLoading;
  const branding = PROVIDER_BRANDING[providerType] || FALLBACK_BRANDING;

  const handleClick = async () => {
    onAttemptStart?.();
    try {
      const authorizationUrl = await authorizeMutation.mutateAsync({
        providerType,
        organisationId,
      });
      // Preserve where the user launched login from (e.g.
      // /oauth/consent?request=…) so SsoCallbackPage can return them there
      // instead of dumping every deep link on "/". Skip /auth/* paths — the
      // login screens themselves must never become the post-login destination.
      const returnPath = window.location.pathname + window.location.search;
      const isAuthScreen =
        window.location.pathname === "/auth" || window.location.pathname.startsWith("/auth/");
      if (!isAuthScreen) {
        window.sessionStorage.setItem(SSO_RETURN_PATH_KEY, returnPath);
      } else {
        // Launched from a login screen: a login begun here must resolve to "/",
        // never to a stale return path a previous, abandoned attempt left behind.
        // Clear any leftover so it can't hijack this fresh login.
        window.sessionStorage.removeItem(SSO_RETURN_PATH_KEY);
      }
      window.location.href = authorizationUrl;
    } catch (error) {
      onError(error instanceof Error ? error : new Error("Failed to initiate SSO login"));
    }
  };

  return (
    <Button
      onClick={handleClick}
      isLoading={isLoading}
      loadingText="Redirecting..."
      w="100%"
      h="42px"
      bg={branding.bg}
      color={branding.color}
      _hover={{ bg: branding.hoverBg }}
      _active={{ bg: branding.activeBg }}
      leftIcon={branding.icon}
      fontWeight="semibold"
      fontSize="sm"
      borderWidth={branding.bg === "white" ? "1px" : 0}
      borderColor="gray.300"
    >
      <Text>{branding.label}</Text>
    </Button>
  );
}
